import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { Controller } from 'react-hook-form'
import { FormControl, InputLabel, MenuItem, Select, Tooltip } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { AddCircle, KeyboardArrowDown } from '@mui/icons-material'
import { useIntl } from 'react-intl'

interface Props {
  name: string
  label?: JSX.Element | string
  placeholder?: string
  control?: any
  options: SelectOption[]
  initialValue?: string | number
  required?: boolean
  disabled?: boolean
  className?: string
  fullWidth?: boolean
  syncChange?: (value: string | number) => void
  dark?: boolean
  showResetOption?: boolean
  formattedSelectedLabel?: string
  title?: string
}

const useStyles = makeStyles(() => ({
  icon: {
    fill: '#fff',
  },
}))

export default function GenericSelect({
  name,
  label,
  placeholder,
  control,
  options,
  initialValue,
  required = false,
  disabled = false,
  className,
  fullWidth = true,
  dark = false,
  showResetOption = true,
  formattedSelectedLabel,
  title,
  syncChange,
}: Props): JSX.Element {
  const [value, setValue] = useState<string | number>(
    initialValue === null || initialValue === undefined ? '' : initialValue,
  )
  const [showTooltip, setShowTooltip] = useState<boolean>(false)
  const intl = useIntl()
  const classes = useStyles()

  useEffect(() => {
    if ((initialValue || initialValue === '') && initialValue !== value) {
      setValue(initialValue)
    }
  }, [initialValue, value])

  const handleChange = (e: any) => {
    const newValue = e.target.value
    setValue(newValue)
    if (syncChange) {
      syncChange(newValue)
    }
    return newValue
  }

  const getPlaceholderText = () => {
    const requiredText = intl.formatMessage({ id: 'strings.required' })

    if (placeholder && required) {
      return `${placeholder} (${requiredText})`
    } else if (placeholder) {
      return placeholder
    } else if (required) {
      return requiredText
    }
    return ''
  }

  const getLabelWithValue = (value: any) => {
    const option = options.filter((option: SelectOption) => value === option.key)[0]
    const ValueElement = dark ? ValueDark : Value
    return option ? <ValueElement>{getLabel(option)}</ValueElement> : ''
  }

  const getLabel = (option: SelectOption) => {
    if (formattedSelectedLabel) {
      return formattedSelectedLabel
    }
    return option.label
  }

  const getValue = () => {
    return value !== '' ? value : ''
  }

  const renderSelect = (onChange?: Function, fieldValue?: any) => {
    const displayValue = fieldValue !== undefined ? fieldValue : getValue()
    return (
      <Tooltip title={title || ''} placement="top" open={showTooltip}>
        <Select
          required={required}
          IconComponent={KeyboardArrowDown}
          onChange={(e) => {
            const newValue = handleChange(e)
            if (onChange) {
              onChange(newValue)
            }
          }}
          variant="outlined"
          displayEmpty
          value={displayValue}
          renderValue={(value: any) =>
            value !== '' ? getLabelWithValue(value) : <Placeholder>{getPlaceholderText()}</Placeholder>
          }
          inputProps={{
            classes: {
              icon: dark ? classes.icon : '',
            },
          }}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          onClick={() => setShowTooltip(false)}
          onOpen={() => setShowTooltip(false)}
        >
          {showResetOption && (
            <ResetValue value="" disabled={getValue() === ''}>
              {intl.formatMessage({ id: 'strings.reset' })}
            </ResetValue>
          )}
          {options.map((option) => (
            <MenuItem key={option.key} value={option.key}>
              <>
                {option.key === 0 && <PlusIcon color="primary" />}
                {option.key === 0 ? <NewOption>{option.label}</NewOption> : option.label}
              </>
            </MenuItem>
          ))}
        </Select>
      </Tooltip>
    )
  }

  const Container = dark ? InputContainerDark : InputContainer

  return (
    <Container margin="normal" fullWidth={fullWidth} required={required} disabled={disabled} className={className}>
      {label && <FieldLabel shrink>{label}</FieldLabel>}
      {control ? (
        <Controller
          control={control}
          name={name}
          defaultValue={initialValue !== undefined && initialValue !== null ? initialValue : ''}
          render={({ field: { onChange, value: fieldValue } }) => renderSelect(onChange, fieldValue)}
        />
      ) : (
        renderSelect()
      )}
    </Container>
  )
}

const InputContainer = styled(FormControl)`
  margin: 10px 0 0 0;

  & .Mui-focused {
    color: inherit;
  }
`

const InputContainerDark = styled(InputContainer)`
  color: ${(props) => props.theme.customPalette.light};
  border: 1px solid;
  border-color: ${(props) => props.theme.customPalette.light};
  border-radius: 5px;
`

const FieldLabel = styled(InputLabel)`
  margin-top: -20px;
  transition: none;
  transform: none;
`

const Placeholder = styled.span`
  color: ${(props) => props.theme.customPalette.mediumGray};
`

const ResetValue = styled(MenuItem)`
  color: ${(props) => props.theme.customPalette.mediumGray};
`

const Value = styled.span``

const ValueDark = styled(Value)`
  color: ${(props) => props.theme.customPalette.light};
`

const PlusIcon = styled(AddCircle)`
  margin: 0 10px 0 0;
`

const NewOption = styled.span`
  font-weight: 500;
`
